<template>
  <section
    v-if="variant === 'simple'"
    class="relative bg-[#1f2937] px-4 sm:px-6 lg:px-20 pt-[114px] pb-10"
  >
    <div :class="[baseContentClass, resolvedContentClass]">
      <nav class="flex items-center gap-0.5" aria-label="Fil d'Ariane">
        <NuxtLink
          to="/"
          class="text-white/90 hover:text-white transition-colors"
        >
          <svg class="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1.5L1.5 6.5V14.5H6V10H10V14.5H14.5V6.5L8 1.5Z" />
          </svg>
        </NuxtLink>
        <template v-for="(link, index) in breadcrumbLinks" :key="index">
          <svg class="w-4 h-4 text-white/70" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 4L10 8L6 12"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <NuxtLink
            :to="link.to"
            class="text-white/70 text-sm font-semibold font-outfit capitalize hover:text-white transition-colors"
          >
            {{ link.label }}
          </NuxtLink>
        </template>
        <svg class="w-4 h-4 text-white/70" viewBox="0 0 16 16" fill="none">
          <path
            d="M6 4L10 8L6 12"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="text-white text-sm font-semibold font-outfit">{{
          breadcrumb
        }}</span>
      </nav>

      <div class="flex flex-col gap-4">
        <h1
          class="text-3xl sm:text-4xl lg:text-[40px] font-semibold text-white font-outfit tracking-[-0.68px] leading-[1.4]"
        >
          {{ title }}
        </h1>
        <p
          v-if="description"
          class="text-base font-medium text-white/70 font-outfit max-w-[486px] leading-6"
        >
          {{ description }}
        </p>
        <slot v-if="$slots['additional-info']" name="additional-info" />
      </div>

      <div v-if="$slots.badges" class="flex items-center flex-wrap">
        <slot name="badges" />
      </div>
    </div>
  </section>

  <section v-else :class="['relative overflow-hidden', sectionHeightClass]">
    <!-- Background Image with Gradient -->
    <div class="absolute inset-0">
      <img
        v-if="backgroundImage"
        :src="backgroundImage"
        alt=""
        :class="[
          'absolute inset-0 h-full w-full',
          imageClass || 'object-cover',
        ]"
      />
      <div
        class="absolute inset-0 sm:hidden"
        style="
          background: linear-gradient(
            180deg,
            rgba(53, 69, 91, 0.6) 0%,
            rgba(53, 69, 91, 0.8) 49.875%,
            rgba(31, 41, 55, 0.9) 73.146%,
            rgba(31, 41, 55, 0.9) 100%
          );
        "
      ></div>
      <div
        class="absolute inset-0 hidden sm:block"
        style="
          background: linear-gradient(
            270deg,
            rgba(53, 69, 91, 0.2) 0%,
            rgba(53, 69, 91, 0.6) 25%,
            rgb(53, 69, 91) 45%,
            rgb(31, 41, 55) 75%,
            rgb(31, 41, 55) 100%
          );
        "
      ></div>
    </div>

    <!-- Content -->
    <div
      class="relative h-full px-0 sm:px-6 lg:px-20 flex flex-col justify-end pb-10"
    >
      <div :class="[baseContentClass, resolvedContentClass]">
        <!-- Breadcrumb -->
        <nav class="flex items-center gap-0.5" aria-label="Fil d'Ariane">
          <NuxtLink
            to="/"
            class="text-white/70 hover:text-white transition-colors"
          >
            <svg class="w-4 h-4" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 1.5L1.5 6.5V14.5H6V10H10V14.5H14.5V6.5L8 1.5Z" />
            </svg>
          </NuxtLink>
          <!-- Parent breadcrumb links -->
          <template v-for="(link, index) in breadcrumbLinks" :key="index">
            <svg class="w-4 h-4 text-white/50" viewBox="0 0 16 16" fill="none">
              <path
                d="M6 4L10 8L6 12"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <NuxtLink
              :to="link.to"
              class="text-white/40 text-sm font-semibold font-outfit capitalize hover:text-white/60 transition-colors"
            >
              {{ link.label }}
            </NuxtLink>
          </template>
          <!-- Current page breadcrumb -->
          <svg class="w-4 h-4 text-white/50" viewBox="0 0 16 16" fill="none">
            <path
              d="M6 4L10 8L6 12"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="text-white text-sm font-semibold font-outfit">{{
            breadcrumb
          }}</span>
        </nav>

        <!-- Title & Description -->
        <div class="flex flex-col gap-4">
          <h1
            class="text-[28px] sm:text-[34px] md:text-[38px] lg:text-[40px] font-semibold text-white font-outfit tracking-[-0.68px] leading-[1.05]"
          >
            {{ title }}
          </h1>
          <p
            v-if="description"
            class="text-[15px] sm:text-[16px] md:text-[17px] font-medium text-white/70 font-outfit max-w-[520px] leading-[1.6]"
          >
            {{ description }}
          </p>
          <slot v-if="$slots['additional-info']" name="additional-info" />
        </div>

        <!-- Badges slot -->
        <div v-if="$slots.badges" class="flex items-center flex-wrap">
          <slot name="badges" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
export interface BreadcrumbLink {
  label: string;
  to: string;
}

interface Props {
  title: string;
  breadcrumb: string;
  description?: string;
  backgroundImage?: string;
  variant?: "image" | "simple";
  /** Parent breadcrumb links before the current breadcrumb */
  breadcrumbLinks?: BreadcrumbLink[];
  /** Controls max width and padding of the inner content */
  contentClass?: string;
  /** Mobile height (e.g., 'h-[271px]') - default: h-[271px] */
  mobileHeight?: string;
  /** Small screen height (e.g., 'sm:h-[381px]') - default: sm:h-[381px] */
  tabletHeight?: string;
  /** Custom class(es) for the background image (e.g., 'object-contain object-right') */
  imageClass?: string;
}

const baseContentClass = "w-full flex flex-col gap-6 sm:gap-8";

const props = withDefaults(defineProps<Props>(), {
  description: undefined,
  backgroundImage: "/images/home-hero.jpg",
  variant: "image",
  breadcrumbLinks: () => [],
  contentClass: undefined,
  mobileHeight: "h-[271px]",
  tabletHeight: "sm:h-[381px]",
  imageClass:
    "object-cover min-[1200px]:object-contain min-[1117px]:object-right",
});

const sectionHeightClass = computed(() => {
  return `${props.mobileHeight} ${props.tabletHeight}`;
});

const resolvedContentClass = computed(() => {
  if (props.contentClass) {
    return props.contentClass;
  }

  return props.variant === "simple"
    ? "max-w-[1280px] mx-auto w-full"
    : "max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-0";
});
</script>
